import { Badge, Button, Container, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { ArrowRightIcon, BrowsersIcon, LockIcon, PasswordIcon, QrCodeIcon } from '@phosphor-icons/react';
import { SeoContent } from '@/components/SeoContent';
import { AdSlot } from '@/components/AdSlot';
import { useStrings } from '@/i18n/useLang';

interface ToolCardProps {
  href: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
  featured?: boolean;
  badge?: string;
}

function ToolCard({ href, icon, title, desc, cta, featured, badge }: ToolCardProps) {
  return (
    <Paper
      component="a"
      href={href}
      withBorder
      radius="lg"
      p="xl"
      style={{
        background: 'var(--bg-card)',
        borderColor: featured ? 'var(--primary)' : 'var(--border)',
        boxShadow: featured ? '0 0 28px var(--primary-subtle)' : undefined,
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        height: '100%',
        position: 'relative',
      }}
    >
      {badge && (
        <Badge variant="filled" color="cyan" size="sm" style={{ position: 'absolute', top: 12, right: 12 }}>
          {badge}
        </Badge>
      )}
      <Group gap="sm">
        <span style={{ color: 'var(--primary)', display: 'inline-flex' }}>{icon}</span>
        <Title order={2} style={{ fontSize: featured ? '1.6rem' : '1.4rem', color: 'var(--text)' }}>
          {title}
        </Title>
      </Group>
      <Text c="dimmed" style={{ flex: 1 }}>
        {desc}
      </Text>
      <Button
        variant={featured ? 'filled' : 'light'}
        color="cyan"
        rightSection={<ArrowRightIcon size={16} />}
        mt="sm"
      >
        {cta}
      </Button>
    </Paper>
  );
}

export function HomePage() {
  const t = useStrings();

  // Only the three popular tools are shown on the landing page; the rest are
  // reachable via the header category menus.
  const featured = [
    { href: '/pwdgen/', icon: <PasswordIcon size={40} weight="duotone" />, title: t.cards.pwdgenTitle, desc: t.cards.pwdgenDesc },
    { href: '/qrgen/', icon: <QrCodeIcon size={40} weight="duotone" />, title: t.cards.qrgenTitle, desc: t.cards.qrgenDesc },
    { href: '/favicongen/', icon: <BrowsersIcon size={40} weight="duotone" />, title: t.cards.favicongenTitle, desc: t.cards.favicongenDesc },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="md" align="center" mb="xl" ta="center">
        <Badge variant="light" color="cyan" size="lg">
          {t.hero.localBadge}
        </Badge>
        <Title order={1} style={{ fontSize: 'clamp(2rem, 6vw, 3.2rem)' }}>
          {t.hero.title}
        </Title>
        <Text size="lg" c="dimmed" maw={640}>
          {t.hero.subtitle}
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg" mt="lg">
        {featured.map((tool) => (
          <ToolCard key={tool.href} {...tool} cta={t.cards.open} featured badge={t.sections.popular} />
        ))}
      </SimpleGrid>

      <Paper
        withBorder
        radius="lg"
        p="xl"
        mt="xl"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border-light)' }}
      >
        <Group gap="sm" mb="xs">
          <LockIcon size={20} color="var(--success)" />
          <Title order={2} style={{ fontSize: '1.3rem' }}>
            {t.trust.title}
          </Title>
        </Group>
        <Text c="dimmed">{t.trust.body}</Text>
      </Paper>

      <AdSlot slot={import.meta.env.VITE_ADSENSE_SLOT_CONTENT} />

      <SeoContent />
    </Container>
  );
}
